import { ArrowBack } from '@mui/icons-material';
import { Box, Button, Card, IconButton, Stack, Typography } from '@mui/material';
import {
    ListBase,
    Pagination,
    Title,
    useCanAccess,
    useGetOne,
} from 'react-admin';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ResourceNotFound } from '@/components/misc/ResourceNotFound';
import { EditProjectNumberDialog } from '@/modules/projects/EditProjectNumberDialog';
import { RelatedPeopleTable } from '@/modules/projects/RelatedPeopleTable';
import { useBrowserTitle } from '@/utils/useBrowserTitle';
import { QuoteListContent } from './QuoteListContent';
import { JobFolderHeader } from './JobFolderHeader';
import { EditProjectContractorDialog } from './EditProjectContractorDialog';
import { QuoteJobFolder } from '@/types';

export const QuoteFolderShow = () => {
    const { projectId } = useParams();
    const id = Number(projectId);
    const [editOpen, setEditOpen] = useState(false);
    const [editContractorOpen, setEditContractorOpen] = useState(false);
    const { canAccess: canCreateQuote } = useCanAccess({
        resource: 'quotes',
        action: 'create',
    });

    const {
        data: folder,
        isPending,
        error,
    } = useGetOne<QuoteJobFolder>(
        'projects',
        { id },
        { enabled: Number.isFinite(id), retry: false },
    );
    useBrowserTitle(
        folder?.project_no != null ? `${folder.project_no} - Quote` : undefined,
    );

    if (!Number.isFinite(id)) {
        return null;
    }

    if (error) {
        return (
            <ResourceNotFound
                resourceLabel="Project"
                listPath="/quotes"
                listLabel="Back to quotes"
            />
        );
    }

    return (
        <Stack gap={2} p={1} sx={{ width: '100%', minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={1}>
                <IconButton component={Link} to="/quotes" aria-label="Back to quote folders">
                    <ArrowBack />
                </IconButton>
                <Title title="Quotes in job folder" />
            </Stack>

            {!isPending && folder && (
                <>
                    <JobFolderHeader
                        folder={folder}
                        listPath="/quotes"
                        onEditProjectNumber={
                            folder.project_no != null && !folder.has_legacy
                                ? () => setEditOpen(true)
                                : undefined
                        }
                        onEditContacts={() => setEditContractorOpen(true)}
                    />
                    {folder.project_no != null && !folder.has_legacy && (
                        <EditProjectNumberDialog
                            open={editOpen}
                            projectId={folder.id}
                            currentProjectNo={folder.project_no}
                            onClose={() => setEditOpen(false)}
                        />
                    )}
                    <EditProjectContractorDialog
                        open={editContractorOpen}
                        folder={folder}
                        onClose={() => setEditContractorOpen(false)}
                    />
                </>
            )}

            <RelatedPeopleTable projectId={id} />
            <Box>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    gap={1}
                    sx={{ px: 1, mb: 1 }}
                >
                    <Typography variant="subtitle1">Quotes</Typography>
                    {canCreateQuote && !folder?.has_legacy ? (
                        <Button
                            component={Link}
                            to={`/quotes/create?project_id=${id}`}
                            variant="contained"
                            size="small"
                            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' }, minHeight: 40 }}
                        >
                            Create quote
                        </Button>
                    ) : null}
                </Stack>
                <ListBase
                    disableAuthentication
                    resource="quotes"
                    filter={{ project_id: id }}
                    sort={{ field: 'id', order: 'DESC' }}
                    perPage={25}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Card>
                            <QuoteListContent linkPrefix="/quotes" selectable={false} showProjectSummary={false} />
                        </Card>
                        <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                    </Box>
                </ListBase>
            </Box>
        </Stack>
    );
};
